"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useEmailPasswordAuth = void 0;
const react_1 = require("react");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
const useEmailPasswordAuth = (options) => {
    const [isLoading, setIsLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const [isSignedIn, setIsSignedIn] = (0, react_1.useState)(false);
    const [nextStep, setNextStep] = (0, react_1.useState)(null);
    const [isSignUpComplete, setIsSignUpComplete] = (0, react_1.useState)(false);
    const [isConfirmationRequired, setIsConfirmationRequired] = (0, react_1.useState)(false);
    const [pendingCredentials, setPendingCredentials] = (0, react_1.useState)(null);
    const handleError = (0, react_1.useCallback)((err) => {
        console.error('Auth Error:', err);
        setError(err);
        if (options?.onError) {
            options.onError(err);
        }
        setPendingCredentials(null);
    }, [options]);
    const signIn = (0, react_1.useCallback)(async (username, password) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await amplifyBootstrap_1.Auth.signIn(username, password);
            setNextStep(result.nextStep?.signInStep || null);
            if (result.isSignedIn) {
                setIsSignedIn(true);
                setIsSignUpComplete(true);
                setIsConfirmationRequired(false);
                if (options?.onSignInSuccess) {
                    options.onSignInSuccess();
                }
            }
            else {
                console.warn(`Sign in attempt for ${username} resulted in next step: ${result.nextStep?.signInStep}`);
            }
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            handleError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [options, handleError]);
    const signUp = (0, react_1.useCallback)(async (username, password, email) => {
        setIsLoading(true);
        setError(null);
        setPendingCredentials(null);
        try {
            const result = await amplifyBootstrap_1.Auth.signUp(username, password, { email });
            if (result.isSignUpComplete) {
                setIsSignUpComplete(true);
                await signIn(username, password);
                if (options?.onSignUpSuccess) {
                    options.onSignUpSuccess();
                }
            }
            else {
                setPendingCredentials({ username, password });
                setIsConfirmationRequired(true);
                console.warn(`Sign up requires confirmation for ${username}. Credentials stored temporarily.`);
            }
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            handleError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [options, handleError, signIn]);
    const confirmSignUp = (0, react_1.useCallback)(async (username, code) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await amplifyBootstrap_1.Auth.confirmSignUp(username, code);
            setIsConfirmationRequired(false);
            if (pendingCredentials && pendingCredentials.username === username) {
                try {
                    const { password } = pendingCredentials;
                    setPendingCredentials(null);
                    const signInResult = await amplifyBootstrap_1.Auth.signIn(username, password);
                    if (signInResult.isSignedIn) {
                        setIsSignedIn(true);
                        setIsSignUpComplete(true);
                        if (options?.onAutoSignInSuccess) {
                            options.onAutoSignInSuccess();
                        }
                        else if (options?.onSignInSuccess) {
                            options.onSignInSuccess();
                        }
                    }
                    else {
                        console.warn(`Auto sign-in after confirmation requires additional steps: ${signInResult.nextStep?.signInStep}`);
                        setIsSignUpComplete(true);
                    }
                }
                catch (signInError) {
                    console.error('Error during auto sign-in after confirmation:', signInError);
                    setIsSignUpComplete(true);
                }
            }
            else {
                console.warn(`Cannot perform auto sign-in after confirmation: credentials not available`);
                setIsSignUpComplete(true);
            }
            if (options?.onSignUpSuccess) {
                options.onSignUpSuccess();
            }
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            handleError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
            setPendingCredentials(null);
        }
    }, [options, handleError, pendingCredentials]);
    return {
        signIn,
        signUp,
        confirmSignUp,
        isLoading,
        error,
        isSignedIn,
        nextStep,
        isSignUpComplete,
        isConfirmationRequired,
    };
};
exports.useEmailPasswordAuth = useEmailPasswordAuth;
